// ⚡️ FIREBASE CHAT OPERATIONS: Firebase operations for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useCallback } from 'react';
import { 
  collection,
  doc,
  setDoc,
  getDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
  getDocs,
  limit,
  startAfter,
  arrayUnion,
  limitToLast
} from 'firebase/firestore';
import { db } from '../../../firebase';
import chatCache from '../../../utils/cache';
import offlineStorage from '../../../utils/offlineStorage';

// Custom hook for Firebase chat operations
export const useFirebaseChat = () => {
  const PAGE_SIZE = 20;

  // Function to create Firebase chat room for agent conversations
  const createAgentChatRoom = useCallback(async (convId, user, dispatch) => {
    if (!convId || !user) return;
    
    
    try {
      // Check if agent chat room already exists
      const agentConvRef = doc(db, 'agentConversations', convId);
      const agentConvDoc = await getDoc(agentConvRef);
      
      if (!agentConvDoc.exists()) {
        // Create new agent conversation document
        const agentConversationData = {
          id: convId,
          type: 'agent',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastMessage: 'Welcome! Your conversation has started 🎉',
          lastMessageTime: serverTimestamp(),
          unreadCount: 0,
          status: 'active',
          
          // User information
          user: {
            uid: user.uid || user.id || user.email, // Use uid, id, or email as fallback
            email: user.email,
            name: user.displayName || user.name || user.email.split('@')[0],
            avatar: user.photoURL || user.avatar || null
          },
          
          // Agent information
          agent: {
            uid: 'admin',
            email: process.env.REACT_APP_ADMIN_EMAIL || 'admin@ytproud.com',
            name: 'YTproud Agent',
            avatar: '/images/ytproud.png', // ✅ FIX: Use YTproud logo for agent
            online: true
          },
          
          // Chat metadata
          participants: [user.email, process.env.REACT_APP_ADMIN_EMAIL || 'admin@ytproud.com'],
          members: [user.uid || user.id || user.email, 'admin'], // Use uid, id, or email as fallback
          
          // Settings
          allowFileUpload: true,
          allowEmojis: true,
          isActive: true
        };
        
        // Create the agent conversation document
        await setDoc(agentConvRef, agentConversationData);
        
        // Create initial welcome message
        const messagesCol = collection(db, 'agentConversations', convId, 'messages');
        const welcomeMessage = {
          id: `welcome_${Date.now()}`,
          text: 'Welcome! Your conversation has started 🎉 You can now start chatting.',
          from: 'admin',
          fromUid: 'admin',
          fromName: 'YTproud Agent',
          timestamp: serverTimestamp(),
          status: 'sent',
          readBy: [user.email],
          type: 'text',
          isSystemMessage: true,
          conversationId: convId
        };
        
        await addDoc(messagesCol, welcomeMessage);
        
        
        // Update local state to reflect the new conversation
        if (dispatch) {
          dispatch({ type: 'SET_FIRESTORE_CONV_ID', payload: convId });
        }
        
      } else {
      }
      
    } catch (error) {
    }
  }, []);

  // Enhanced loadMessages with dual-cache system
  const loadMessages = useCallback(async (convId, opts = {}, dispatch, firestoreMessages) => {
    if (!convId) return;
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Check memory cache first (fastest)
    if (!opts.startAfterDoc && !opts.forceRefresh) {
      const memoryCachedMessages = chatCache.getCachedMessages(convId);
      if (memoryCachedMessages && memoryCachedMessages.length > 0) {
        if (dispatch) {
          dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: memoryCachedMessages });
        }
        
        // Load fresh data in background (non-blocking)
        setTimeout(() => loadMessagesFromServer(convId, opts, dispatch, firestoreMessages), 50);
        return;
      }
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Fallback to IndexedDB cache
      try {
        const indexedDBCachedMessages = await offlineStorage.getCachedMessages(convId);
        if (indexedDBCachedMessages && indexedDBCachedMessages.length > 0) {
          if (dispatch) {
            dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: indexedDBCachedMessages });
          }
          
          // Update memory cache with IndexedDB data
          chatCache.cacheMessages(convId, indexedDBCachedMessages);
          
          // Load fresh data in background (non-blocking)
          setTimeout(() => loadMessagesFromServer(convId, opts, dispatch, firestoreMessages), 100);
          return;
        }
      } catch (error) {
      }
    }
    
    // Load from server
    await loadMessagesFromServer(convId, opts, dispatch, firestoreMessages);
  }, []);

  // Load messages from Firestore server
  const loadMessagesFromServer = useCallback(async (convId, opts = {}, dispatch, firestoreMessages) => {
    // Only show loading state when loading more messages (scrolling up)
    if (opts.startAfterDoc && dispatch) {
      dispatch({ type: 'SET_LOADING_MORE', payload: true });
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Use limitToLast for faster initial load
    let q;
    if (opts.startAfterDoc) {
      // For pagination (loading older messages)
      q = query(
        collection(db, 'agentConversations', convId, 'messages'),
        orderBy('timestamp', 'desc'),
        startAfter(opts.startAfterDoc),
        limit(PAGE_SIZE)
      );
    } else {
      // ⚡️ OPTIMIZATION: Load only last 30 messages initially for faster load
      q = query(
        collection(db, 'agentConversations', convId, 'messages'),
        orderBy('timestamp', 'desc'),
        limitToLast(30) // Load only last 30 messages for instant display
      );
    }
    
    let msgs = [];
    let snap = null;
    try {
      snap = await getDocs(q);
      msgs = snap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      });
    } catch (error) {
      
      // If it's a timestamp error, try to load without pagination
      if (error.message.includes('server timestamp')) {
        try {
          const fallbackQuery = query(
            collection(db, 'agentConversations', convId, 'messages'),
            orderBy('timestamp', 'desc'),
            limitToLast(30)
          );
          const fallbackSnap = await getDocs(fallbackQuery);
          let fallbackMsgs = fallbackSnap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
          });
          
          if (!opts.startAfterDoc) {
            fallbackMsgs = fallbackMsgs.reverse();
          }
          
          if (dispatch) {
            if (opts.append) {
              dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, ...fallbackMsgs] });
            } else {
              dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: fallbackMsgs });
            }
            dispatch({ type: 'SET_LOADING_MORE', payload: false });
          }
          return;
        } catch (fallbackError) {
        }
      }
      
      // Set empty messages on error
      if (dispatch) {
        if (opts.append) {
          dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages] });
        } else {
          dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [] });
        }
        dispatch({ type: 'SET_LOADING_MORE', payload: false });
      }
      return;
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Reverse messages for correct display order
    if (!opts.startAfterDoc) {
      msgs = msgs.reverse(); // Show newest messages at bottom
    }
    
    if (dispatch) {
      if (opts.append) {
        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, ...msgs] });
      } else {
        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: msgs });
        // ⚡️ PERFORMANCE OPTIMIZATION: Cache the messages in both memory and IndexedDB
        try {
          // Cache in memory (fastest access)
          chatCache.cacheMessages(convId, msgs);
          // Cache in IndexedDB (persistent storage)
          await offlineStorage.cacheMessages(convId, msgs);
          
          // ⚡️ PERFORMANCE OPTIMIZATION: Preload next batch of messages in background
          // ✅ FIX: Only preload if we have a valid document with committed timestamp
          if (msgs.length > 0 && !opts.startAfterDoc && snap && snap.docs && snap.docs.length > 0) {
            const lastDoc = snap.docs[snap.docs.length - 1];
            const lastDocData = lastDoc.data();
            
            // Only preload if the document has a committed timestamp (not serverTimestamp)
            if (lastDocData.timestamp && typeof lastDocData.timestamp.toDate === 'function') {
              setTimeout(() => {
                loadMessages(convId, { startAfterDoc: lastDoc, append: true }, dispatch, firestoreMessages);
              }, 2000); // Preload after 2 seconds
            }
          }
        } catch (error) {
        }
      }
      
      // ✅ FIX: Add safety check before using snap
      if (snap && snap.docs && snap.docs.length > 0) {
        dispatch({ type: 'SET_LAST_VISIBLE', payload: snap.docs[snap.docs.length - 1] });
        dispatch({ type: 'SET_HAS_MORE', payload: snap.docs.length === (opts.startAfterDoc ? PAGE_SIZE : 30) });
      } else {
        dispatch({ type: 'SET_LAST_VISIBLE', payload: null });
        dispatch({ type: 'SET_HAS_MORE', payload: false });
      }
      dispatch({ type: 'SET_LOADING_MORE', payload: false });
    }
  }, [loadMessages]);

  // Function for marking messages as read
  const markMessageAsRead = useCallback(async (messageId, conversationId, user, conversation, isAdminDashboard, dispatch) => {
    if (!user || !messageId || !conversationId) {
      return;
    }

    try {
      let messageRef;
      // Use correct collection for agent or user-to-user chat
      if (
        (conversation && conversation.id === 'admin') ||
        isAdminDashboard ||
        (conversationId && conversationId.startsWith('agent_'))
      ) {
        messageRef = doc(db, 'agentConversations', conversationId, 'messages', messageId);
      } else {
        messageRef = doc(db, 'sellerbuyerconversations', conversationId, 'messages', messageId);
      }

      // Get current message data
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        return;
      }

      const messageData = messageDoc.data();
      const currentReadBy = messageData.readBy || [];
      
      // Only update if not already read by this user
      if (!currentReadBy.includes(user.email)) {
        await updateDoc(messageRef, {
          readBy: arrayUnion(user.email),
          status: 'read'
        });
      }

      if (dispatch) {
        dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { [messageId]: 'read' } });
      }
    } catch (error) {
    }
  }, []);

  // Function to update message status when sending
  const updateMessageStatus = useCallback(async (messageId, conversationId, status, dispatch) => {
    if (!messageId || !conversationId) return;
    
    try {
      const messageRef = doc(db, 'agentConversations', conversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        status: status
      });
      if (dispatch) {
        dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { [messageId]: status } });
      }
    } catch (error) {
    }
  }, []);

  return {
    createAgentChatRoom,
    loadMessages,
    loadMessagesFromServer,
    markMessageAsRead,
    updateMessageStatus,
    PAGE_SIZE
  };
};
